import { FunctionComponent, ReactElement, useCallback, useState } from 'react'
import useStyles from './styles'
import Modal from '@/components/Modal'
import Icon from '@/components/HeroIcons'
import { Button, Input } from '@conexasaude/hero'

import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useRealtimeAttendanceContext } from '../../contexts/RealtimeAttendanceContext'
import { useTokenAmil } from '../../store/useTokenAmil'

import { returnFormattedDate } from '@/utils/fnUtils'

import { validateCallToken } from '../../realtime-attendance.services'

const errorMessages = (
  errorType: 'wrongToken' | 'emptyToken' | 'errorInValidation',
  message?: string,
): string => {
  const messages = {
    wrongToken: `Código incorreto`,
    emptyToken: `Digite o código`,
    errorInValidation: `Erro ao validar token: ${message}`,
  }
  return messages[errorType]
}

const chooseIcon = (icon: 'loading' | 'check' | 'none'): ReactElement => {
  const icons = {
    loading: (
      <Icon name="loading" themeColor={['neutral', '500']} size="24px" />
    ),
    validated: (
      <Icon name="check" themeColor={['success', '500']} size="24px" />
    ),
    none: null,
  }
  return icons[icon]
}

const CallStartedModalWithToken: FunctionComponent<{
  onActionClick: (callback: () => void) => void
}> = ({ onActionClick }) => {
  const { modalContainer, footer } = useStyles()
  const [token, setToken] = useState<string>('')
  const [validated, setValidated] = useState<boolean>()
  const [isLoading, setIsLoading] = useState<boolean>()
  const [error, setError] = useState<boolean>()
  const [errorMessage, setErrorMessage] = useState<string>(null)
  const [icon, setIcon] = useState<ReactElement>(null)
  const guideNumber = useTokenAmil(state => state.guideNumber)

  const {
    realtimeAttendance: { call },
  } = useRealtimeAttendanceContext()
  const { clinic, user } = useAuthContext()
  const { closeModal } = useModalContext()

  const checkToken = (token: string) => {
    setIsLoading(true)
    setIcon(chooseIcon('loading'))
    validateCallToken(
      call?.attendanceId.toString(),
      call?.professionalId.toString(),
      returnFormattedDate(),
      token,
      guideNumber,
    )
      .then(() => {
        setError(false)
        setValidated(true)
        setIcon(chooseIcon('check'))
      })
      .catch(err => {
        setValidated(false)
        setIcon(chooseIcon('none'))
        setError(true)
        setErrorMessage(
          errorMessages('errorInValidation', err.response.data.msg),
        )
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const resetToken = useCallback(() => {
    setToken('')
    setValidated(false)
  }, [])

  const handleTokenChange = token => {
    setToken(token)
    setValidated(false)
    if (token.length === 6) {
      checkToken(token)
    } else {
      setIcon(chooseIcon('none'))
    }
  }

  return (
    <Modal id="CALL_STARTED_WITH_TOKEN" onDismiss={null}>
      <div css={modalContainer}>
        <p>
          Para iniciar a consulta com{' '}
          <span className="highlight">{call?.professionalName}</span>, insira o
          código token da {clinic?.friendlyName || user?.clinic.name}, que foi
          enviado por e-mail ou SMS para o número de telefone cadastrado na
          plataforma {clinic?.friendlyName || user?.clinic.name};
        </p>
        <p>
          <span>
            O código token também está disponível na área “Tokens” do aplicativo{' '}
            {clinic?.friendlyName || user?.clinic.name}
          </span>
        </p>
        <div className="input-section">
          <Input
            value={token}
            onChange={e => handleTokenChange(e.target.value)}
            label={`Token de segurança`}
            placeholder="Digite o código aqui"
            errorMessage={errorMessage}
            error={!!error}
            validated={!!validated}
            icon={!token ? chooseIcon('none') : icon}
            required
            disabled={isLoading}
            inputProps={{ readOnly: validated }}
          />
        </div>
        <div css={footer}>
          <Button
            onClick={() => {
              closeModal()
              onActionClick(resetToken)
            }}
            disabled={!validated || !token}
          >
            Iniciar consulta
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default CallStartedModalWithToken
